// The POSITION paper — an election, as one member sees it.
//
// Sibling of ../BallotPaper.tsx (the motion paper), and deliberately not a
// variant of it: an election has candidates, places and No Confidence, and the
// two papers share a vocabulary (`.vote-row`, `.ballot-*`, <CastStatus>) rather
// than a component with a mode flag. Everything the motion paper's header
// explains about native <fieldset>/<legend>/<input type="radio">, about the
// filled-vs-hollow state dot, and about there being no tally on a voting paper
// holds here unchanged and is not restated.
//
// What is particular to an election, each of it from CAST-PATH.md:205-232:
//
//  • NO CONFIDENCE IS A ROW LIKE ANY OTHER. Same element, same class, same size
//    and weight, no colour, no icon, no separator. It is built by
//    `positionRows` alongside the candidates for exactly that reason — there is
//    no branch here that could give it a different look. SO 15.7 makes it a
//    lawful choice on every election, and a red row would tell a member that
//    choosing it is an error.
//  • A BLANK PAPER IS A SEPARATE ACT AND NOT A FOURTH ROW (registrar ruling
//    2026-08-27). <ReturnBlankAction> below the rows opens a warning screen that
//    REPLACES the paper; nothing is sent until it is confirmed. The argument —
//    including why this does not contradict the No Confidence rule above it — is
//    at the head of ./BlankConfirm.tsx, which also holds every word of it.
//  • NO FREE-TEXT FIELD, ANYWHERE. There is no <input> in this file that is not
//    `type="radio"`, and there must never be one. A write-in box on a secret
//    ballot is a de-anonymisation channel with a keyboard attached.
//  • MARKING IS FREE, SILENT AND LOCAL. Nothing in this component calls the
//    network. The mark lives in the parent's state until the member submits, so
//    there is exactly one moment of network exposure under time pressure.
//  • 48px MINIMUM TARGETS — `--decides-row-min`, above WCAG 2.2 SC 2.5.8's 24px
//    and the estate's 44px floor, because this is a once-only binding choice.
//  • NO COUNTDOWN, NO TURNOUT, NO TALLY. There is no clock on this paper and no
//    figure of any kind; the props it takes cannot supply one.
//  • AN ITEM THIS HOUSE MAY NOT DECIDE IS SHOWN, UNMARKABLE, NEVER OMITTED
//    (registrar ruling 2026-08-20). See <UnmarkableItems> below: every paper on
//    one ballot carries the same items, so the shape of a paper never says which
//    House is holding it.
//  • AN ATTRIBUTABLE BALLOT SAYS SO BEFORE THE FIRST ROW IS MARKED. The value
//    is `ballot.secrecy` — the ballot row's own, never a request parameter — so
//    a secret ballot cannot be made to look published, nor the reverse, by any
//    caller.
//
// It is a RENDERER and it decides nothing. Whether this member may hold this
// paper at all is settled server-side on the request that issued it (the roll,
// the ballot state, SO 15.8's electorate), and re-settled on the request that
// records it.

import { useId, type FormEvent, type JSX } from 'react';
import { CastStatus, type CastAct } from '../CastStatus';
import {
  ELECTORATE_SENTENCE,
  isElectorate,
  paperHouseLabel,
  POSITION_MARK_INSTRUCTION,
  POSITION_ROW_NOTE,
  seatSentence,
  UNMARKABLE_ITEM_NOTE,
  type MarkableItem,
  type PositionRow,
} from '../paper';
import type { CastItem, CastPaperView } from '../api/client';
import { ReturnBlankAction } from './BlankConfirm';

/**
 * THE ITEMS ON THIS BALLOT THAT THIS HOUSE MAY NOT DECIDE — listed, named, and
 * carrying no control of any kind.
 *
 * **Registrar ruling 2026-08-20: an unentitled item is served and rendered
 * UNMARKABLE, never omitted.** The route sends every item on the ballot to every
 * elector, each with `entitled`, because a paper that was simply SHORT of an
 * item would itself be a discriminator — the shape of the paper would say which
 * House is holding it. The same reasoning applies to what is on the screen, so
 * this block exists rather than a `filter`.
 *
 * IT CONTAINS NO `<input>`, NO BUTTON AND NO LINK, which is what "unmarkable"
 * means here. There is nothing to disable and therefore nothing to re-enable: an
 * item that is not the reader's cannot be marked by any state this component can
 * be put into.
 */
export function UnmarkableItems({
  heading,
  items,
}: {
  heading: string;
  items: readonly CastItem[];
}): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section className="ballot-unmarkable">
      <h2 className="ballot-legend">{heading}</h2>
      {items.map((item) => (
        <div key={item.itemId} className="ballot-instrument">
          <p className="ballot-instrument-text">
            <strong>{item.title}</strong>
          </p>
          <p className="ballot-note">
            {/* SO 15.8's sentence where the electorate is a word this build can
                name, and nothing where it is not — the note below says the
                operative thing on its own, and a half-built sentence about who
                elects a position is worse than none. */}
            {isElectorate(item.electorate) ? `${ELECTORATE_SENTENCE[item.electorate]} ` : ''}
            {UNMARKABLE_ITEM_NOTE}
          </p>
        </div>
      ))}
    </section>
  );
}

interface PositionPaperProps {
  paper: CastPaperView;
  /** The one item on this paper this member may MARK, narrowed by ./CastPage.
   *  A paper with more than one, or none, never reaches this component. */
  item: MarkableItem;
  /** Built once by the parent, so the row the member marked and the mark sent
   *  to the server are read from the SAME list. Two calls to `positionRows`
   *  would be two lists that agree until the day they do not. */
  rows: PositionRow[];
  /** The other items on this ballot — the ones this House may not decide.
   *  Rendered unmarkable beneath the vote, never dropped. */
  unmarkable: CastItem[];
  /** Who the member is, in their own words — the address they signed in with.
   *  The roll holds no names (see the register's account of why), so this is the
   *  honest answer to "you are voting as", and it comes from the session rather
   *  than from the paper. */
  voterLabel: string;
  /** The marked row's key, or null. Owned by the parent because the submit is. */
  marked: string | null;
  onMark: (key: string) => void;
  onSubmit: () => void;
  /** Opens the blank-paper warning. It SENDS NOTHING — see ./BlankConfirm. It
   *  is offered unconditionally, never only on an unmarked paper: a control
   *  that appeared only when nothing was marked would tell anyone watching the
   *  screen that this member has marked nothing. */
  onReturnBlank: () => void;
  submitting: boolean;
  act: CastAct;
}

export function PositionPaper({
  paper,
  item,
  rows,
  unmarkable,
  voterLabel,
  marked,
  onMark,
  onSubmit,
  onReturnBlank,
  submitting,
  act,
}: PositionPaperProps): JSX.Element {
  const groupName = useId();
  const houseLabel = paperHouseLabel(paper.paper.voterHouse);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form className="ballot" onSubmit={handleSubmit}>
      {/* The BALLOT this paper belongs to, then that it is taking papers. Shape
          and word together, never colour alone.

          A PAPER IN HAND IS THE PROOF, and that is why there is no `state` field
          on this response and no clock on this screen. The route's gate 5
          refuses to issue a paper at all unless the poll is open, so holding one
          is what "Open" says. If the President closes the poll while a member is
          still deciding, the SUBMIT is refused and the status line says so at
          the moment it matters — a countdown here would be a pressure device,
          and a dot that went hollow under a member's thumb would be a clock by
          another name. */}
      <p className="ballot-eyebrow">
        <span>{paper.ballot.title}</span>
        <span className="ballot-state">
          <span className="ballot-dot" aria-hidden="true" />
          <span className="ballot-sr">Voting </span>
          Open
        </span>
      </p>

      <h1 className="ballot-title">{item.title}</h1>

      {/* Who you are voting AS. The House is not decoration: it decides which
          tally your paper lands in (SO 8.6), so it belongs on the paper rather
          than in a menu. It is the House STAMPED ON THE PAPER and not the one on
          the roll — on a secret ballot the Bishop votes on a clergy-stamped
          paper (registrar ruling 2026-08-20) — and where the stamp is a word
          this build cannot name, the address stands alone rather than beside a
          blank. */}
      <p className="ballot-elector-lead">You are voting as</p>
      <p className="ballot-elector">
        {voterLabel}
        {houseLabel === null ? '' : ` · ${houseLabel}`}
      </p>

      {/* NOT A SECRET BALLOT — said before a single row is marked, and only ever
          on the ballot row's own `secrecy`. On an attributable ballot the paper
          NAMES its elector in the database
          (`paper_elector_contact_id_iff_attributable`) and the vote is published
          with the result, which is a thing a member must know while they can
          still decide what to do about it. The secret case says nothing: this
          whole surface is built for it, and a badge reading "secret" would be
          chrome on every paper the platform will ever issue. */}
      {paper.ballot.secrecy === 'attributable' && (
        <p className="alert">
          <strong>This ballot is not secret.</strong> Your vote is recorded against your name and is
          published with the result.
        </p>
      )}

      <hr className="ballot-rule" />

      {/* What is being filled and who fills it — the election's equivalent of the
          motion paper's instrument, and above the rows for the same reason the
          yardstick is: on a phone, anything below them is off-screen at the exact
          scroll position where the choice is marked. */}
      <div className="ballot-instrument">
        <p className="ballot-instrument-text">
          {seatSentence(item.alternateSeats)} {ELECTORATE_SENTENCE[item.electorate]}
        </p>
      </div>

      <hr className="ballot-rule" />

      <fieldset className="ballot-votes">
        <legend className="ballot-legend">Your vote</legend>
        <p className="ballot-instruction">{POSITION_MARK_INSTRUCTION}</p>
        {rows.map((row) => (
          // ONE MAP, ONE SHAPE. Candidates, No Confidence and Abstain are the
          // same element with the same class — there is no branch here, so there
          // is nowhere for a colour or an icon to be added to one of them.
          <label className="vote-row" key={row.key}>
            <input
              className="radio vote-radio"
              type="radio"
              name={groupName}
              value={row.key}
              checked={marked === row.key}
              // Locked only while a submission is in flight. There is no
              // "closed" state to lock for: a paper is issued only on an open
              // poll, and a poll that closes mid-decision is met at the submit.
              disabled={submitting}
              onChange={() => {
                onMark(row.key);
              }}
            />
            <span className="vote-label">{row.label}</span>
          </label>
        ))}
        <p className="ballot-note">{POSITION_ROW_NOTE}</p>
      </fieldset>

      {/* BELOW the vote, because it is not part of it — and on the paper,
          because leaving it off would make the paper's own shape a statement
          about which House is holding it. */}
      {unmarkable.length > 0 && <hr className="ballot-rule" />}
      <UnmarkableItems heading="Also on this ballot" items={unmarkable} />

      {/* THE OTHER LAWFUL WAY OFF THIS PAPER (registrar ruling 2026-08-27).
          Below the vote and below the items that are not this reader's, behind
          the paper's own rule, and OUTSIDE the action bar — it is a second act,
          not a second submit. It sends nothing; it opens the warning. The whole
          argument for a separate act rather than a fourth row, and for why that
          does not break the No Confidence rule, is at the head of
          ./BlankConfirm.tsx. */}
      <hr className="ballot-rule" />
      <ReturnBlankAction onAsk={onReturnBlank} disabled={submitting} />

      {/* ABOVE the action bar: the bar is pinned over the bottom of the scroll
          port on a phone, so a message after it is a message nobody reads. It is
          this paper's only live region, and it is incapable of carrying the mark
          — ../CastStatus is a closed union of act tokens, with no string prop. */}
      <CastStatus act={act} />

      <div className="action-bar ballot-actions">
        <button
          type="submit"
          className="btn btn-primary btn-block ballot-submit"
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Submit my vote'}
        </button>
      </div>
    </form>
  );
}
